import React, {useContext, useEffect, useState} from 'react';

import {CommentListResult} from '../../../../api-client';
import {createCommentManager} from '../../../../di';
import {useErrorCallback} from '../../../../hooks';
import {PaginationLoadMore} from '../../../pagination';
import Comment from '../Comment';
import CommentForm from '../CommentForm';
import {DetailedProjectContext} from '../DetailedProjectContext/DetailedProjectContext';
import './ProjectChat.scoped.scss';
import styles from './ProjectChat.module.scss';

const INITIAL_PAGE = 1;
const COMMENTS_PER_PAGE = 5;

const ProjectChat = () => {
  const project = useContext(DetailedProjectContext).project;
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(INITIAL_PAGE);
  const handleError = useErrorCallback();
  const [commentListResult, setCommentListResult] = useState<CommentListResult>({
    comments: [],
    totalCount: 0,
    chatAvailable: false,
  });

  const loadData = async (pageToLoad: number, perPage: number, append = false) => {
    setLoading(true);
    try {
      const provider = await createCommentManager();
      const nextResult = await provider.getList(project.uuid, pageToLoad, perPage);

      setCommentListResult((prev) => {
        if (!append || pageToLoad === INITIAL_PAGE) {
          return nextResult;
        }

        return {
          ...nextResult,
          comments: [...prev.comments, ...nextResult.comments],
        };
      });
    } catch (err: any) {
      await handleError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(INITIAL_PAGE, COMMENTS_PER_PAGE, false);
  }, [project.uuid]);

  const handleCommentCreated = () => {
    setPage(INITIAL_PAGE);
    loadData(INITIAL_PAGE, COMMENTS_PER_PAGE, false);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadData(nextPage, COMMENTS_PER_PAGE, true);
  };

  const handleReset = () => {
    setPage(INITIAL_PAGE);
    loadData(INITIAL_PAGE, COMMENTS_PER_PAGE, false);
  };

  const isEmpty = commentListResult.comments.length === 0 && !commentListResult.chatAvailable;

  if (isEmpty) return null;

  return (
    <div className="project-chat">
      <div className="project-chat__title">Чат проекта</div>
      <div className={styles.projectChat__commentbox}>
        {commentListResult.chatAvailable && (
          <CommentForm projectUuid={project.uuid} onCreated={handleCommentCreated} />
        )}
        <ul className={styles.projectChat__list}>
          {commentListResult.comments.map((comment) => (
            <Comment
              comment={comment}
              projectUuid={project.uuid}
              key={comment.uuid}
              onCommentCreated={handleCommentCreated}
              chatAvailable={commentListResult.chatAvailable}
            />
          ))}
        </ul>
        <PaginationLoadMore
          loaded={commentListResult.comments.length}
          total={commentListResult.totalCount}
          page={page}
          loading={loading}
          onLoadMore={handleLoadMore}
          onReset={handleReset}
        />
      </div>
    </div>
  );
};

export default ProjectChat;
